import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Mail, ArrowLeft } from 'lucide-react'
import OTPInput from '@components/web/OTPInput'
import Button from '@components/web/Button'
import { providerAuthService } from '@lib/supabase/providerAuth.service'

export default function ProviderEmailVerification() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const email = searchParams.get('email') ?? ''

  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendLoading, setResendLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleVerify = async () => {
    if (otp.length < 6) { setError('Please enter the 6-digit code'); return }
    setError('')
    setIsLoading(true)
    const result = await providerAuthService.verifyEmail(email, otp)
    setIsLoading(false)

    if (!result.success) {
      setError(result.error || 'Invalid code. Please try again.')
      return
    }

    setSuccess(true)
    setTimeout(() => navigate('/auth/provider-identity-verification'), 1500)
  }

  const handleResend = async () => {
    setResendLoading(true)
    await providerAuthService.resendVerificationEmail(email)
    setResendLoading(false)
    setResendCooldown(60)
    setOtp('')
    setError('')
  }

  return (
    <div className="bg-card rounded-2xl shadow-lg p-8 w-full">
      <Link to="/auth/provider-signup" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail size={28} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Verify Your Email</h2>
        <p className="text-muted-foreground text-sm mt-2">We sent a 6-digit verification code to</p>
        <p className="font-semibold text-foreground text-sm">{email}</p>
      </div>

      {success && (
        <div className="mb-4 rounded-lg border border-primary/20 bg-primary/10 p-3 text-center">
          <p className="text-sm font-medium text-primary">✓ Email verified successfully!</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive text-center">{error}</p>
        </div>
      )}

      <div className="mb-6">
        <OTPInput value={otp} onChange={setOtp} />
      </div>

      <Button onClick={handleVerify} className="w-full" size="lg" isLoading={isLoading} disabled={success}>
        Verify Email
      </Button>

      <div className="text-center mt-4">
        <p className="text-sm text-muted-foreground">Didn't receive the code?</p>
        {resendCooldown > 0 ? (
          <p className="text-sm text-muted-foreground mt-1">Resend in {resendCooldown}s</p>
        ) : (
          <button onClick={handleResend} disabled={resendLoading} className="text-sm text-primary font-semibold hover:underline mt-1 disabled:opacity-50">
            {resendLoading ? 'Sending...' : 'Resend Code'}
          </button>
        )}
      </div>
    </div>
  )
}
